import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import {
  Button,
  Card,
  LinearProgress,
  Typography,
  Box,
  Stack,
  Alert,
  Input,
  FormControl,
  FormLabel,
} from '@mui/joy';
import { useCallback, useState } from 'react';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from '../../../components/Snackbar/SnackbarContext';
import { sendOutboundWhatsApp } from '../../../api/api';
import classNames from 'classnames';

const DEFAULT_MEETING_ID = 'dmc_client_intake';

export function OutboundWhatsApp() {
  const [uploadingFile, setUploadingFile] = useState(false);
  const [sendingMessages, setSendingMessages] = useState(false);
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([]);
  const [manualPhoneNumber, setManualPhoneNumber] = useState<string>('');
  const [results, setResults] = useState<{ success: number; failed: number; errors: string[] }>({
    success: 0,
    failed: 0,
    errors: []
  });
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();

  const parseCSVFile = (file: File): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvContent = e.target?.result as string;
          const lines = csvContent.split('\n');
          const numbers: string[] = [];
          
          lines.forEach((line, index) => {
            const trimmedLine = line.trim();
            if (trimmedLine && index > 0) { // Skip header row
              const columns = trimmedLine.split(',');
              const phoneNumber = columns[0]?.trim().replace(/['"]/g, ''); // Remove quotes
              if (phoneNumber && phoneNumber.match(/^\+?[\d\s\-\(\)]+$/)) {
                // Basic phone number validation
                const cleanNumber = phoneNumber.replace(/\s+/g, '').replace(/-/g, '').replace(/\(/g, '').replace(/\)/g, '');
                if (!cleanNumber.startsWith('+')) {
                  numbers.push('+' + cleanNumber);
                } else {
                  numbers.push(cleanNumber);
                }
              }
            }
          });
          resolve(numbers);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const onDrop = useCallback(
    async (files: File[]) => {
      setUploadingFile(true);
      setResults({ success: 0, failed: 0, errors: [] });
      
      try {
        const file = files[0];
        if (!file) {
          showErrorSnackbar('No file selected');
          return;
        }

        const numbers = await parseCSVFile(file);
        setPhoneNumbers(numbers);
        setAcceptedFiles([file]);
        
        if (numbers.length === 0) {
          showErrorSnackbar('No valid phone numbers found in CSV');
        } else {
          showSuccessSnackbar(`Found ${numbers.length} phone numbers in CSV`);
        }
      } catch (error) {
        console.error('Error parsing CSV:', error);
        showErrorSnackbar('Error parsing CSV file');
      } finally {
        setUploadingFile(false);
      }
    },
    []
  );

  const handleAddManualNumber = () => {
    if (!manualPhoneNumber.trim()) {
      showErrorSnackbar('Please enter a phone number');
      return;
    }

    const cleanNumber = manualPhoneNumber.trim().replace(/\s+/g, '').replace(/-/g, '').replace(/\(/g, '').replace(/\)/g, '');
    const formattedNumber = cleanNumber.startsWith('+') ? cleanNumber : '+' + cleanNumber;
    
    if (!formattedNumber.match(/^\+?[\d]+$/)) {
      showErrorSnackbar('Please enter a valid phone number');
      return;
    }

    if (phoneNumbers.includes(formattedNumber)) {
      showErrorSnackbar('Phone number already added');
      return;
    }

    setPhoneNumbers([...phoneNumbers, formattedNumber]);
    setManualPhoneNumber('');
    showSuccessSnackbar('Phone number added successfully');
  };

  const handleSendMessages = async () => {
    if (phoneNumbers.length === 0) {
      showErrorSnackbar('No phone numbers to send messages to');
      return;
    }

    setSendingMessages(true);
    let successCount = 0;
    let failedCount = 0;
    const errors: string[] = [];

    try {
      for (const phoneNumber of phoneNumbers) {
        try {
          const result = await sendOutboundWhatsApp({
            meetingId: DEFAULT_MEETING_ID,
            customerWhatsApp: phoneNumber
          });
          
          if (result.conversationSid) {
            successCount++;
          } else {
            failedCount++;
            errors.push(`Failed to send to ${phoneNumber}: No conversation ID returned`);
          }
        } catch (error) {
          failedCount++;
          errors.push(`Failed to send to ${phoneNumber}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      setResults({ success: successCount, failed: failedCount, errors });
      
      if (successCount > 0) {
        showSuccessSnackbar(`Successfully sent ${successCount} messages`);
      }
      if (failedCount > 0) {
        showErrorSnackbar(`Failed to send ${failedCount} messages`);
      }
    } catch (error) {
      console.error('Error sending messages:', error);
      showErrorSnackbar('Error sending messages');
    } finally {
      setSendingMessages(false);
    }
  };

  const handleReset = () => {
    setAcceptedFiles([]);
    setPhoneNumbers([]);
    setManualPhoneNumber('');
    setResults({ success: 0, failed: 0, errors: [] });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: uploadingFile || sendingMessages,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv']
    },
    multiple: false
  });

  return (
    <Box sx={{ p: 3, maxWidth: '800px', mx: 'auto' }}>
      <Typography level="h2" gutterBottom>
        WhatsApp Outbound Messages
      </Typography>
      
      <Typography level="body-md" sx={{ mb: 3 }}>
        Upload a CSV file with phone numbers to send outbound WhatsApp messages. 
        The CSV should have phone numbers in the first column.
      </Typography>

      <Stack spacing={3}>
        {/* Manual Input Section */}
        <Card variant="outlined" sx={{ p: 3 }}>
          
          <Stack direction="row" spacing={2} alignItems="end">
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Add Phone Number Manually</FormLabel>
              <Input
                placeholder="Enter phone number (e.g., +919876543210)"
                value={manualPhoneNumber}
                onChange={(e) => setManualPhoneNumber(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddManualNumber();
                  }
                }}
                disabled={sendingMessages}
              />
            </FormControl>
            <Button 
              onClick={handleAddManualNumber}
              disabled={!manualPhoneNumber.trim() || sendingMessages}
            >
              Add Number
            </Button>
          </Stack>
        </Card>

        {/* File Upload Section */}
        <Card variant="outlined" sx={{ p: 3 }}>
          <Typography level="title-md" gutterBottom>
            Or Upload CSV File
          </Typography>
          
          <div
            className={classNames({
              'dropzone disabled': uploadingFile || sendingMessages,
            })}
            {...getRootProps()}
            style={{
              border: '2px dashed #ccc',
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
              cursor: uploadingFile || sendingMessages ? 'not-allowed' : 'pointer',
              backgroundColor: isDragActive ? '#f5f5f5' : 'transparent',
              opacity: uploadingFile || sendingMessages ? 0.5 : 1
            }}
          >
            <input {...getInputProps()} />
            <UploadFileOutlinedIcon
              sx={{ 
                fontSize: '40px', 
                color: 'primary.main',
                opacity: uploadingFile || sendingMessages ? 0.5 : 1
              }}
            />
            <Typography level="body-md" sx={{ mt: 1 }}>
              {isDragActive
                ? 'Drop the CSV file here...'
                : 'Drag and drop a CSV file here, or click to select'}
            </Typography>
            <Typography level="body-xs" color="neutral">
              Only CSV files are supported
            </Typography>
          </div>

          {uploadingFile && <LinearProgress sx={{ mt: 2 }} />}

          {acceptedFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography level="title-sm" gutterBottom>
                Selected File
              </Typography>
              <Typography level="body-xs">
                {acceptedFiles[0].name}
              </Typography>
              <Typography level="body-xs" color="success">
                Found {phoneNumbers.length} phone numbers
              </Typography>
            </Box>
          )}
        </Card>

        {/* Phone Numbers Preview */}
        {phoneNumbers.length > 0 && (
          <Card variant="outlined" sx={{ p: 3 }}>
            <Typography level="title-md" gutterBottom>
              Phone Numbers ({phoneNumbers.length})
            </Typography>
            <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
              {phoneNumbers.slice(0, 10).map((number, index) => (
                <Typography key={index} level="body-xs">
                  {number}
                </Typography>
              ))}
              {phoneNumbers.length > 10 && (
                <Typography level="body-xs" color="neutral">
                  ... and {phoneNumbers.length - 10} more
                </Typography>
              )}
            </Box>
          </Card>
        )}

        {/* Results Section */}
        {(results.success > 0 || results.failed > 0) && (
          <Card variant="outlined" sx={{ p: 3 }}>
            <Typography level="title-md" gutterBottom>
              Results
            </Typography>
            <Stack spacing={1}>
              {results.success > 0 && (
                <Alert color="success">
                  Successfully sent {results.success} messages
                </Alert>
              )}
              {results.failed > 0 && (
                <Alert color="danger">
                  Failed to send {results.failed} messages
                </Alert>
              )}
              {results.errors.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography level="title-sm" gutterBottom>
                    Error Details:
                  </Typography>
                  <Box sx={{ maxHeight: '150px', overflow: 'auto' }}>
                    {results.errors.map((error, index) => (
                      <Typography key={index} level="body-xs" color="danger">
                        {error}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Stack>
          </Card>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={2}>
          <Button
            onClick={handleSendMessages}
            disabled={phoneNumbers.length === 0 || sendingMessages}
            loading={sendingMessages}
            startDecorator={<FileUploadOutlinedIcon />}
          >
            {sendingMessages ? 'Sending Messages...' : `Send Messages to ${phoneNumbers.length} Numbers`}
          </Button>
          
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={uploadingFile || sendingMessages}
          >
            Reset
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}